import { useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/store/auth'
import { useEffectiveClientId } from '@/hooks/useAuth'
import { useSpendSeeds } from '@/hooks/useSeeds'
import type { Headline, SwipeDecision, Story, StoryVersion, Voice } from '@/types/database'
import { functionError } from '@/hooks/useMix'

/**
 * The swipe deck's data.
 *
 * Headlines are written by the edge function under the service role; the client
 * only reads them and records decisions. Everything here reads from the database
 * rather than calling the edge function directly, so the deck keeps working
 * offline-ish and a refetch is cheap.
 */

/** Headlines not yet swiped by this user, newest batch first. */
export function useHeadlineDeck() {
  const clientId = useEffectiveClientId()
  const userId = useAuthStore((s) => s.user?.id)

  return useQuery({
    queryKey: ['headline-deck', clientId, userId],
    enabled: !!clientId && !!userId,
    queryFn: async (): Promise<Headline[]> => {
      // Decisions are per user, so two people on one account each get their own
      // deck rather than one of them finding it already empty.
      const { data: decided, error: dErr } = await supabase
        .from('headline_decisions')
        .select('headline_id')
        .eq('client_id', clientId!)
        .eq('user_id', userId!)
      if (dErr) throw dErr

      const seen = new Set((decided ?? []).map((d) => d.headline_id))

      const { data, error } = await supabase
        .from('headlines')
        .select('*')
        .eq('client_id', clientId!)
        .order('fetched_at', { ascending: false })
        .limit(60)
      if (error) throw error

      return (data as Headline[]).filter((h) => !seen.has(h.id))
    },
  })
}

/**
 * Record a swipe.
 *
 * Costs nothing: navigation is not consumption. A left swipe matters as much as
 * a right one, because the reject stream is the ranking signal and cannot be
 * reconstructed later.
 */
export function useSwipe() {
  const clientId = useEffectiveClientId()
  const userId = useAuthStore((s) => s.user?.id)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (args: { headlineId: string; decision: SwipeDecision }) => {
      const { error } = await supabase.from('headline_decisions').insert({
        headline_id: args.headlineId,
        client_id: clientId!,
        user_id: userId ?? null,
        decision: args.decision,
      })
      // Swiping the same card twice is a duplicate key, not a failure worth
      // surfacing: the decision is already recorded.
      if (error && !/duplicate key/i.test(error.message)) throw error
    },
    onMutate: async (args) => {
      // Remove the card immediately. Waiting for the round trip would make the
      // deck feel sticky on every swipe.
      const key = ['headline-deck', clientId, userId]
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<Headline[]>(key)
      queryClient.setQueryData<Headline[]>(key, (old) =>
        (old ?? []).filter((h) => h.id !== args.headlineId)
      )
      return { previous, key }
    },
    onError: (_err, _args, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(ctx.key, ctx.previous)
    },
  })
}

/** Stories already built, most recent first. */
export function useStories() {
  const clientId = useEffectiveClientId()
  return useQuery({
    queryKey: ['stories', clientId],
    enabled: !!clientId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('stories')
        .select('*')
        .eq('client_id', clientId!)
        .order('created_at', { ascending: false })
      if (error) throw error
      return data as Story[]
    },
  })
}

export function useStory(storyId: string | undefined) {
  return useQuery({
    queryKey: ['story', storyId],
    enabled: !!storyId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('stories').select('*').eq('id', storyId!).single()
      if (error) throw error
      return data as Story
    },
  })
}

export type BuildStage = 'reading' | 'verifying' | 'crafting' | 'tone' | 'harvesting'

/** Where each real stage lands on the sprout's 0 to 100, so its five status
 *  lines follow the work rather than a timer. */
export const STAGE_PERCENT: Record<BuildStage, number> = {
  reading: 8, verifying: 24, crafting: 44, tone: 72, harvesting: 86,
}

/**
 * Build a story from a headline. 12 seeds.
 *
 * Streams: the function reports each real stage as it reaches it, so the load
 * screen can grow with the work. The seed spend happens in the function, after
 * the story is safely written, so a failed build costs nothing.
 */
export function useBuildStory() {
  const clientId = useEffectiveClientId()
  const queryClient = useQueryClient()
  const swipe = useSwipe()

  return useMutation({
    mutationFn: async (args: { headlineId: string; onStage?: (stage: BuildStage) => void }) => {
      // Record the right swipe first so the card leaves the deck even if the
      // build is slow.
      await swipe.mutateAsync({ headlineId: args.headlineId, decision: 'right' })

      const { data: { session } } = await supabase.auth.getSession()
      const base = import.meta.env.VITE_SUPABASE_URL as string
      const res = await fetch(`${base}/functions/v1/build-story`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token ?? ''}`,
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
        },
        body: JSON.stringify({
          client_id: clientId,
          headline_id: args.headlineId,
          // Derived from the headline, so a retry charges once.
          idempotency_key: `build:${args.headlineId}`,
          stream: true,
        }),
      })
      if (!res.ok || !res.body) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body?.error ?? 'That build did not go through. Try it again.')
      }

      const reader = res.body.getReader()
      const dec = new TextDecoder()
      let buf = ''
      let story: Story | null = null
      for (;;) {
        const { value, done } = await reader.read()
        if (value) buf += dec.decode(value, { stream: true })
        let nl: number
        while ((nl = buf.indexOf('\n')) >= 0) {
          const line = buf.slice(0, nl).trim()
          buf = buf.slice(nl + 1)
          if (!line) continue
          const ev = JSON.parse(line) as { stage: string; story?: Story; message?: string; status?: number }
          if (ev.stage === 'done') story = ev.story ?? null
          else if (ev.stage === 'error') {
            const err = new Error(ev.message ?? 'That build did not finish.')
            if (ev.status === 402 || /insufficient seeds/i.test(ev.message ?? '')) err.name = 'InsufficientSeedsError'
            throw err
          } else args.onStage?.(ev.stage as BuildStage)
        }
        if (done) break
      }
      if (!story) throw new Error('That build did not finish. You were not charged.')
      return { story }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stories', clientId] })
      queryClient.invalidateQueries({ queryKey: ['seed-balance', clientId] })
    },
  })
}

/** Regenerate an article or script in the story's current voice. 3 seeds. */
export function useRegenerate() {
  const clientId = useEffectiveClientId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (args: { storyId: string; target: 'article' | 'script' }) => {
      const { data, error } = await supabase.functions.invoke('regenerate', {
        body: {
          client_id: clientId,
          story_id: args.storyId,
          target: args.target,
          // Time-bucketed: a genuine second regeneration should charge again,
          // but a double tap within the same second should not.
          idempotency_key: `regen:${args.storyId}:${args.target}:${Math.floor(Date.now() / 1000)}`,
        },
      })
      if (error) throw await functionError(error)
      return data as { story: Story }
    },
    onSuccess: (_d, args) => {
      queryClient.invalidateQueries({ queryKey: ['story', args.storyId] })
      queryClient.invalidateQueries({ queryKey: ['story-versions', args.storyId] })
      queryClient.invalidateQueries({ queryKey: ['seed-balance', clientId] })
    },
  })
}

/** Every voice this story has been written in. The original is always kept. */
export function useStoryVersions(storyId: string | undefined) {
  return useQuery({
    queryKey: ['story-versions', storyId],
    enabled: !!storyId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('story_versions').select('*').eq('story_id', storyId!)
        .order('created_at', { ascending: true })
      if (error) throw error
      return data as StoryVersion[]
    },
  })
}

/**
 * Show the story in another voice. A voice already built switches free; a new
 * one is written from the same facts for 1 seed (Amendment A). Research is
 * never re-run.
 */
export function useBuildVoice() {
  const clientId = useEffectiveClientId()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (args: { storyId: string; voice: Voice }) => {
      const { data, error } = await supabase.functions.invoke('build-voice', {
        body: { story_id: args.storyId, voice: args.voice },
      })
      if (error) throw await functionError(error)
      return data as { story: Story; charged: number }
    },
    onSuccess: (_d, args) => {
      queryClient.invalidateQueries({ queryKey: ['story', args.storyId] })
      queryClient.invalidateQueries({ queryKey: ['story-versions', args.storyId] })
      queryClient.invalidateQueries({ queryKey: ['stories', clientId] })
      queryClient.invalidateQueries({ queryKey: ['seed-balance', clientId] })
    },
  })
}

/** Pull a fresh batch of headlines. Free on first round, 3 seeds to reroll. */
export function useFetchHeadlines() {
  const clientId = useEffectiveClientId()
  const queryClient = useQueryClient()
  const spend = useSpendSeeds()

  return useMutation({
    mutationFn: async (args: { reroll?: boolean } = {}) => {
      if (args.reroll) {
        await spend.mutateAsync({
          action: 'reroll',
          // A reroll is a deliberate repeat action, so it is bucketed by minute
          // rather than made permanently idempotent.
          idempotencyKey: `reroll:${clientId}:${Math.floor(Date.now() / 60000)}`,
        })
      }
      const { data, error } = await supabase.functions.invoke('headlines', {
        body: { client_id: clientId, reroll: !!args.reroll },
      })
      if (error) throw await functionError(error)
      return data as { count: number; batch_id: string }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['headline-deck', clientId] })
      queryClient.invalidateQueries({ queryKey: ['seed-balance', clientId] })
    },
  })
}


/**
 * Pre-warms the deck in the background.
 *
 * A pull takes 20 seconds at best and sometimes over a minute, because Emma
 * grounds on only about half of calls and the function retries until she does.
 * Blocking app open on that would mean staring at a spinner every morning.
 *
 * So: show whatever is already stored immediately, and only fetch when the deck
 * is genuinely empty. The edge function has its own freshness window, so a
 * needless call returns the existing batch rather than spending anything.
 */
export function usePrewarmDeck(enabled: boolean) {
  // isFetched, not isLoading. While the query is still disabled, waiting on the
  // client id, react-query reports isLoading false and data undefined, which
  // read as "the deck is empty" and fired a twenty second headline pull on
  // every single app open, before the deck had been looked at.
  const { data: deck, isFetched } = useHeadlineDeck()
  const fetchHeadlines = useFetchHeadlines()
  // Guard against a second fire while the first is still in flight: the query
  // refetches on success, which would otherwise re-trigger this effect.
  const started = useRef(false)

  useEffect(() => {
    if (!enabled || !isFetched || started.current) return
    if ((deck?.length ?? 0) > 0) return
    started.current = true
    // Deliberately not awaited and not surfaced: this is a background top up.
    // A failure here must leave the deck's own empty state to explain itself
    // rather than throwing an error over a screen the user just opened.
    fetchHeadlines.mutate({}, { onError: () => { started.current = false } })
  }, [enabled, isFetched, deck, fetchHeadlines])

  return { warming: fetchHeadlines.isPending }
}
